import React from 'react';

import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeliveryIcon from '@mui/icons-material/DirectionsBike';
import MenusIcon from '@mui/icons-material/FastfoodOutlined';
import Hidden from '@mui/material/Hidden';
import { type Theme, alpha, lighten } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import createStyles from '@mui/styles/createStyles';
import withStyles, { type WithStyles } from '@mui/styles/withStyles';
import clsx from 'clsx';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import CreditNote from '../../../../assets/images/CreditNote.svg';
import PercentageDiscount from '../../../../assets/images/PercentageDiscount.svg';
import GenericTableSkeleton from '../../../../ui/GenericTable/GenericTableSkeleton';
import type { AssignedVoucher } from '../../types/assignedVouchers.types';
import { VoucherType } from '../../types/assignedVouchers.types';

type InnerProps = MappedProps;
type OuterProps = {
  assignedVouchers?: AssignedVoucher[];
  isLoading?: boolean;
};
type Props = InnerProps & OuterProps & WithStyles<typeof styles>;

type VoucherRow = {
  type: string;
  code: string;
  details: string;
  status: 'Valid' | 'NotYetValid' | 'Used' | 'Expired' | 'Disabled' | string;
  voucherId: string;
  isEnabled?: boolean;
};

const mapAssignedToRow = (voucher: AssignedVoucher): VoucherRow => ({
  type: voucher.voucherType,
  code: voucher.code ?? '',
  details: voucher.description ?? '',
  status: voucher.status,
  voucherId: String(voucher.voucherId),
  isEnabled: voucher.isEnabled,
});

const styles = (theme: Theme) =>
  createStyles({
    table: {
      width: '100%',
    },
    cell: {
      padding: theme.spacing(2),
      borderBottomColor: alpha(theme.palette.text.primary, 0.21),
    },
    headCell: {
      fontStyle: 'normal',
      fontStretch: 'normal',
      lineHeight: '2',
    },
    bodyCell: {
      fontWeight: 'normal',
      fontStyle: 'normal',
      fontStretch: 'normal',
      lineHeight: '1.85',
      letterSpacing: 'normal',
    },
    cellLink: {
      display: 'block',
      textDecoration: 'none',
      color: 'inherit',
      outline: 0,
    },
    row: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: lighten(theme.palette.primary.light, 0.85),
      },
    },
    statusValid: {
      color: `${alpha('#25ca74', 0.87)}!important`,
    },
    statusUsed: {
      color: `${theme.palette.primary.light}!important`,
    },
    statusExpired: {
      color: `${alpha(theme.palette.secondary.main, 0.87)}!important`,
    },
    statusDisabled: {
      color: `${alpha(theme.palette.text.primary, 0.4)}!important`,
    },
    type: {
      width: theme.spacing(5),
      height: theme.spacing(5),
      borderRadius: '100%',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      [theme.breakpoints.down('md')]: {
        margin: 0,
      },
    },
    typeValid: {
      backgroundColor: lighten('#25ca74', 0.35),
    },
    typeUsed: {
      backgroundColor: lighten(theme.palette.primary.light, 0.5),
    },
    typeExpired: {
      backgroundColor: theme.palette.secondary.light,
    },
    typeDisabled: {
      backgroundColor: theme.palette.divider,
    },
    typeNotYetValid: {
      backgroundColor: theme.palette.primary.main,
    },
    typeCell: {
      width: `${100 / 15}%`,
    },
    codeCell: {
      width: `${(4 * 100) / 15}%`,
    },
    detailsCell: {
      width: `${100 / 3}%`,
    },
    responsiveTable: {
      paddingTop: theme.spacing(1),
      marginTop: theme.spacing(3),
    },
    responsiveCell: {
      padding: theme.spacing(2),
      display: 'flex',
    },
    responsiveCellLink: {
      display: 'block',
      textDecoration: 'none',
      color: 'inherit',
    },
    responsiveCellContents: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1),
      flexGrow: 1,
      flexShrink: 1,
      flexBasis: '0%',
    },
    responsiveCode: {
      fontSize: '16px',
      fontWeight: 'normal',
      fontStyle: 'normal',
      fontStretch: 'normal',
      lineHeight: 1.5,
      letterSpacing: '0.2px',
      color: theme.palette.text.primary,
    },
    responsiveDescription: {
      fontSize: '12px',
      fontWeight: 'normal',
      fontStyle: 'normal',
      fontStretch: 'normal',
      lineHeight: 1.33,
      letterSpacing: '0.4px',
      color: theme.palette.text.secondary,
    },
    iconColour: {
      color: theme.palette.common.white,
    },
  });

const typeIcon = (type: string, classes: WithStyles<typeof styles>['classes']): React.ReactNode => {
  switch (type) {
    case VoucherType.PercentageDiscount:
      return <img src={PercentageDiscount} />;
    case VoucherType.LumpDiscount:
      return <AttachMoneyIcon className={classes.iconColour} />;
    case VoucherType.AddItem:
      return <MenusIcon className={classes.iconColour} data-fd="menu-icon" />;
    case VoucherType.FreeDelivery:
      return <DeliveryIcon className={classes.iconColour} data-fd="freeDelivery-icon" />;
    case VoucherType.CreditNote:
      return <img src={CreditNote} />;
    default:
      return null;
  }
};

export const CustomerDetailsVouchersTable: React.FC<Props> = (props) => {
  const { classes, translate, assignedVouchers, isLoading } = props;

  const vouchers: VoucherRow[] = (assignedVouchers || []).map(mapAssignedToRow);

  const getTypeVariantClass = (voucher: VoucherRow): string => {
    if (voucher.isEnabled === false) return classes.typeDisabled;
    return classes[`type${voucher.status}` as keyof typeof classes] || classes.typeDisabled;
  };

  const getStatusVariantClass = (voucher: VoucherRow): string => {
    if (voucher.isEnabled === false) return classes.statusDisabled;
    return classes[`status${voucher.status}` as keyof typeof classes] || classes.statusDisabled;
  };

  if (isLoading) {
    return <GenericTableSkeleton loaded={false} />;
  }

  if (vouchers.length === 0) {
    return <GenericTableSkeleton loaded={true} message="No_assigned_vouchers" />;
  }

  return (
    <>
      <Hidden mdDown>
        <Table className={classes.table} data-fd="customer-vouchers-table">
          <TableHead>
            <TableRow>
              <TableCell className={clsx(classes.headCell, classes.cell, classes.typeCell)}>
                {translate('Type')}
              </TableCell>
              <TableCell className={clsx(classes.headCell, classes.cell, classes.codeCell)}>
                {translate('Code')}
              </TableCell>
              <TableCell className={clsx(classes.headCell, classes.cell, classes.detailsCell)}>
                {translate('Details')}
              </TableCell>
              <TableCell className={clsx(classes.headCell, classes.cell, classes.detailsCell)}>
                {translate('Status')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vouchers.map((voucher, index) => (
              <TableRow className={classes.row} key={`${voucher.voucherId}`} data-fd={voucher.code}>
                <TableCell className={clsx(classes.bodyCell, classes.cell, classes.typeCell)}>
                  <Link
                    to={`/${props.AppId}/vouchers/${voucher.voucherId}`}
                    className={classes.cellLink}
                  >
                    <div className={clsx(classes.type, getTypeVariantClass(voucher))}>
                      {typeIcon(voucher.type, classes)}
                    </div>
                  </Link>
                </TableCell>
                <TableCell className={clsx(classes.bodyCell, classes.cell, classes.codeCell)}>
                  <Link
                    to={`/${props.AppId}/vouchers/${voucher.voucherId}`}
                    className={classes.cellLink}
                  >
                    {voucher.code}
                  </Link>
                </TableCell>
                <TableCell className={clsx(classes.bodyCell, classes.cell, classes.detailsCell)}>
                  <Link
                    to={`/${props.AppId}/vouchers/${voucher.voucherId}`}
                    className={classes.cellLink}
                  >
                    {voucher.details}
                  </Link>
                </TableCell>
                <TableCell
                  className={clsx(
                    classes.bodyCell,
                    classes.cell,
                    classes.detailsCell,
                    getStatusVariantClass(voucher)
                  )}
                >
                  <Link
                    to={`/${props.AppId}/vouchers/${voucher.voucherId}`}
                    className={classes.cellLink}
                  >
                    {voucher.isEnabled === false
                      ? translate('Disabled')
                      : translate(voucher.status)}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Hidden>

      <Hidden mdUp>
        <div className={classes.responsiveTable}>
          {vouchers.map((voucher, index) => (
            <Link
              to={`/${props.AppId}/vouchers/${voucher.voucherId}`}
              className={classes.responsiveCellLink}
              key={`${voucher.voucherId}`}
              data-fd={voucher.code}
            >
              <div className={clsx(classes.responsiveCell, classes.row)}>
                <div className={clsx(classes.type, getTypeVariantClass(voucher))}>
                  {typeIcon(voucher.type, classes)}
                </div>
                <div className={classes.responsiveCellContents}>
                  <Typography variant="h4" component="h4" className={classes.responsiveCode}>
                    {voucher.code}
                  </Typography>
                  <Typography variant="h6" component="h6" className={classes.responsiveDescription}>
                    {voucher.details}
                  </Typography>
                </div>
                <Typography
                  variant="h6"
                  component="h6"
                  className={clsx(classes.responsiveDescription, getStatusVariantClass(voucher))}
                >
                  {voucher.isEnabled === false ? translate('Disabled') : translate(voucher.status)}
                </Typography>
              </div>
            </Link>
          ))}
        </div>
      </Hidden>
    </>
  );
};

type MappedProps = ReturnType<typeof mapStateToProps> & { translate: (id: string) => string };
const mapStateToProps = (state: AppState) => {
  return {
    AppId: state.currentApp.AppId as string,
    translate: getTranslate(state.locale),
  } as const;
};

export default connect(mapStateToProps)(
  withStyles(styles, { name: 'CustomerDetailsVouchersTable' })(CustomerDetailsVouchersTable)
);
